import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight, Plus, X, Repeat, History as HistoryIcon, Grid3x3 } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import CrossPostDetailCard from '../../components/social/CrossPostDetailCard';
import CrossPostEditModal from '../../components/social/CrossPostEditModal';

function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

// Buckets by LOCAL calendar day, not UTC — toISOString() converts to UTC
// first, so for any timezone ahead of UTC (e.g. IST, +5:30) a post scheduled
// in the early morning lands on the previous day's cell instead (confirmed:
// 2026-08-26 02:00 IST → toISOString() gives "2026-08-25"). The calendar
// grid itself (monthGrid) is built from local Date components, so the
// bucketing key has to match.
function dayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const STATUS_DOT = {
  draft: 'bg-[#706E6B]',
  scheduled: 'bg-[#0176D3]',
  publishing: 'bg-[#B85C00]',
  published: 'bg-[#2E844A]',
  partially_published: 'bg-[#B85C00]',
  failed: 'bg-[#C23934]',
  cancelled: 'bg-[#706E6B]',
};

export default function ContentCalendarPage() {
  const today = new Date();
  const [view, setView] = useState('calendar'); // 'calendar' | 'history'
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [error, setError] = useState('');

  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const HISTORY_PAGE_SIZE = 20;

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const cells = useMemo(() => monthGrid(year, month), [year, month]);

  useEffect(() => {
    if (view === 'calendar') loadPosts();
  }, [year, month, view]);

  useEffect(() => {
    if (view === 'history') loadHistory();
  }, [view, historyPage]);

  const loadPosts = async () => {
    setLoading(true);
    setError('');
    try {
      // cells[] holds LOCAL midnight for each visible day — the last cell's
      // midnight, sent as-is, cuts off almost the entire final day for any
      // timezone ahead of UTC (confirmed: for IST, a post scheduled 10am on
      // the last visible day fell after the `to` bound and was silently
      // dropped from the fetched range entirely, not just mis-bucketed).
      // End-of-day on the last cell covers the whole local day instead.
      const from = cells[0]?.toISOString() || new Date(year, month, 1).toISOString();
      const lastCell = cells[cells.length - 1];
      const to = lastCell
        ? new Date(lastCell.getFullYear(), lastCell.getMonth(), lastCell.getDate(), 23, 59, 59, 999).toISOString()
        : new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString();
      const res = await api.get('/social/cross-posts', { params: { from, to } });
      setPosts(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load scheduled posts');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    setError('');
    try {
      const res = await api.get('/social/cross-posts/history', {
        params: { limit: HISTORY_PAGE_SIZE, skip: historyPage * HISTORY_PAGE_SIZE },
      });
      setHistory(res.data.data?.items || []);
      setHistoryTotal(res.data.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load post history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const postsByDay = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      if (!p.scheduledAt) return;
      const key = dayKey(new Date(p.scheduledAt));
      (map[key] = map[key] || []).push(p);
    });
    return map;
  }, [posts]);

  const cancelPost = async (id) => {
    const ok = await alertService.confirm('Cancel post', 'This also stops the recurring series if it repeats.');
    if (!ok) return;
    try {
      await api.post(`/social/cross-posts/${id}/cancel`);
      await Promise.all([loadPosts(), view === 'history' ? loadHistory() : Promise.resolve()]);
      refreshSelectedDay();
    } catch (err) {
      alertService.error(err.response?.data?.error || 'Failed to cancel post');
    }
  };

  const deletePost = async (id) => {
    const ok = await alertService.confirm('Delete post', 'This removes it from your CRM history permanently. It does not unpublish it from Instagram/LinkedIn if already live.');
    if (!ok) return;
    try {
      await api.delete(`/social/cross-posts/${id}`);
      await Promise.all([loadPosts(), view === 'history' ? loadHistory() : Promise.resolve()]);
      refreshSelectedDay();
    } catch (err) {
      alertService.error(err.response?.data?.error || 'Failed to delete post');
    }
  };

  const retryPost = async (id) => {
    try {
      await api.post(`/social/cross-posts/${id}/retry`);
      await Promise.all([loadPosts(), view === 'history' ? loadHistory() : Promise.resolve()]);
      refreshSelectedDay();
    } catch (err) {
      alertService.error(err.response?.data?.error || 'Failed to retry post');
    }
  };

  const onPostSaved = async () => {
    setEditingPost(null);
    await Promise.all([loadPosts(), view === 'history' ? loadHistory() : Promise.resolve()]);
    refreshSelectedDay();
  };

  const refreshSelectedDay = () => setSelectedDay((prev) => (prev ? new Date(prev) : prev));

  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const selectedKey = selectedDay ? dayKey(selectedDay) : null;
  const selectedPosts = selectedKey ? postsByDay[selectedKey] || [] : [];

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays size={18} className="text-[#0176D3]" />
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">Content Calendar</h2>
          <div className="flex items-center gap-1 ml-3 bg-[#F3F2F2] rounded-[4px] p-0.5">
            <button
              onClick={() => setView('calendar')}
              className={`flex items-center gap-1 text-[12px] font-semibold px-2 py-1 rounded-[4px] ${
                view === 'calendar' ? 'bg-white text-[#0176D3] shadow-sm' : 'text-[#5C5C5C]'
              }`}
            >
              <Grid3x3 size={13} />
              Calendar
            </button>
            <button
              onClick={() => setView('history')}
              className={`flex items-center gap-1 text-[12px] font-semibold px-2 py-1 rounded-[4px] ${
                view === 'history' ? 'bg-white text-[#0176D3] shadow-sm' : 'text-[#5C5C5C]'
              }`}
            >
              <HistoryIcon size={13} />
              History
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {view === 'calendar' && (
            <>
              <button
                onClick={() => setCursor(new Date(year, month - 1, 1))}
                className="p-1.5 border border-[#E5E5E5] rounded-[4px] hover:border-[#0176D3] text-[#5C5C5C]"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="text-[13px] font-semibold text-[#2E2E2E] w-36 text-center">{monthLabel}</div>
              <button
                onClick={() => setCursor(new Date(year, month + 1, 1))}
                className="p-1.5 border border-[#E5E5E5] rounded-[4px] hover:border-[#0176D3] text-[#5C5C5C]"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
                className="text-[12px] font-semibold text-[#0176D3] hover:underline px-1"
              >
                Today
              </button>
            </>
          )}
          <Link
            to="/dashboard/social/cross-post"
            className="flex items-center gap-1 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold px-3 py-1.5 rounded-[4px]"
          >
            <Plus size={14} />
            New Post
          </Link>
        </div>
      </div>

      {error && <div className="text-[13px] text-[#BA0517]">{error}</div>}

      {view === 'calendar' && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-7 border-b border-[#E5E5E5] bg-[#F3F2F2]">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="text-[11px] font-semibold text-[#706E6B] px-2 py-2 text-center">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-7">
            {cells.map((d, i) => {
              const inMonth = d.getMonth() === month;
              const key = dayKey(d);
              const dayPosts = postsByDay[key] || [];
              const isToday = key === dayKey(today);
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDay(d)}
                  className={`min-h-[92px] border-b border-r border-[#E5E5E5] p-1.5 text-left align-top flex flex-col gap-1 hover:bg-[#F5FAFF] ${
                    inMonth ? 'bg-white' : 'bg-[#FAFAFA]'
                  }`}
                >
                  <span
                    className={`text-[12px] font-semibold w-5 h-5 flex items-center justify-center rounded-full ${
                      isToday ? 'bg-[#0176D3] text-white' : inMonth ? 'text-[#2E2E2E]' : 'text-[#C9C7C5]'
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  <div className="flex flex-col gap-0.5 overflow-hidden">
                    {dayPosts.slice(0, 3).map((p) => (
                      <div
                        key={p._id}
                        className="text-[10px] font-semibold px-1 py-0.5 rounded truncate flex items-center gap-1 bg-[#F3F2F2] text-[#2E2E2E]"
                        title={p.caption}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[p.status] || 'bg-[#706E6B]'}`} />
                        {p.recurrence?.frequency !== 'none' && <Repeat size={9} className="flex-shrink-0" />}
                        <span className="truncate">{p.caption || '(media only)'}</span>
                      </div>
                    ))}
                    {dayPosts.length > 3 && (
                      <div className="text-[10px] text-[#5C5C5C] px-1">+{dayPosts.length - 3} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === 'calendar' && loading && <div className="text-[13px] text-[#5C5C5C]">Loading…</div>}

      {view === 'history' && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 space-y-3">
          {historyLoading && <div className="text-[13px] text-[#5C5C5C]">Loading…</div>}
          {!historyLoading && history.length === 0 && (
            <div className="text-[13px] text-[#5C5C5C]">No posts yet — create one from Cross-Post.</div>
          )}
          {history.map((p) => (
            <CrossPostDetailCard key={p._id} post={p} onCancel={cancelPost} onDelete={deletePost} onEdit={setEditingPost} onRetry={retryPost} />
          ))}
          {historyTotal > HISTORY_PAGE_SIZE && (
            <div className="flex items-center justify-between pt-2 border-t border-[#E5E5E5]">
              <button
                onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                disabled={historyPage === 0}
                className="text-[12px] font-semibold text-[#0176D3] disabled:text-[#C9C7C5] disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-[11px] text-[#5C5C5C]">
                {historyPage * HISTORY_PAGE_SIZE + 1}–{Math.min(historyTotal, (historyPage + 1) * HISTORY_PAGE_SIZE)} of {historyTotal}
              </span>
              <button
                onClick={() => setHistoryPage((p) => (((p + 1) * HISTORY_PAGE_SIZE) < historyTotal ? p + 1 : p))}
                disabled={(historyPage + 1) * HISTORY_PAGE_SIZE >= historyTotal}
                className="text-[12px] font-semibold text-[#0176D3] disabled:text-[#C9C7C5] disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {selectedDay && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={() => setSelectedDay(null)}>
          <div
            className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md max-h-[100dvh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">
                {selectedDay.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h3>
              <button onClick={() => setSelectedDay(null)} className="text-[#5C5C5C] hover:text-[#2E2E2E]">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-3">
              {selectedPosts.length === 0 && (
                <div className="text-[13px] text-[#5C5C5C]">No posts scheduled for this day.</div>
              )}
              {selectedPosts.map((p) => (
                <CrossPostDetailCard key={p._id} post={p} onCancel={cancelPost} onDelete={deletePost} onEdit={setEditingPost} onRetry={retryPost} />
              ))}
            </div>
          </div>
        </div>
      )}

      {editingPost && (
        <CrossPostEditModal post={editingPost} onClose={() => setEditingPost(null)} onSaved={onPostSaved} />
      )}
    </div>
  );
}
